import React, { useState } from 'react';
import SignaturePad from './SignaturePad';

const OfferLetterSigning = ({ offerLetterUrl, onComplete }) => {
  const [formData, setFormData] = useState({
    initials: '',
    signature: '',
    printedName: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState('review'); // 'review' | 'sign'
  const [hasReviewed, setHasReviewed] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const userId = offerLetterUrl.split('/').pop();
  const pdfUrl = `${BACKEND}/api/onboarding/offer-letter/${userId}?token=${token}`;

  const handleSubmit = async () => {
    if (!formData.initials) {
      alert('Please provide your initials');
      return;
    }
    if (!formData.signature) {
      alert('Please provide your signature');
      return;
    }
    if (!formData.printedName) {
      alert('Please provide your printed name');
      return;
    }

    setLoading(true);
    try {
      await onComplete(formData);
    } catch (error) {
      alert('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 mb-6 border border-gray-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Employment Offer Letter</h1>
              <p className="text-gray-500 mt-1">Review your offer letter, then sign to accept</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentStep('review')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentStep === 'review'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                1. Review Document
              </button>
              <button
                onClick={() => { if (hasReviewed) setCurrentStep('sign'); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentStep === 'sign'
                    ? 'bg-blue-600 text-white'
                    : hasReviewed
                    ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    : 'bg-gray-100 text-slate-700 cursor-not-allowed'
                }`}
              >
                2. Sign & Accept
                {hasReviewed && currentStep !== 'sign' && <span className="ml-1 text-green-500">*</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Step 1: Review the PDF */}
        {currentStep === 'review' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600 font-medium">Your Offer Letter</p>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Open in new tab
                </a>
              </div>
              <div className="w-full" style={{ height: '75vh', minHeight: '500px' }}>
                <iframe
                  src={pdfUrl}
                  title="Offer Letter"
                  className="w-full h-full border-0"
                  style={{ minHeight: '500px' }}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => { setHasReviewed(true); setCurrentStep('sign'); }}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-base flex items-center gap-2 shadow-md"
              >
                I've Reviewed — Proceed to Sign
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Sign */}
        {currentStep === 'sign' && (
          <div className="space-y-6">
            {/* Initials */}
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 border border-gray-200">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-9 h-9 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">1</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Your Initials</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    By providing your initials, you acknowledge that you have read and understood all terms in the offer letter.
                  </p>
                </div>
              </div>
              <SignaturePad
                label="Your Initials"
                onSave={(signatureData) => setFormData({...formData, initials: signatureData})}
              />
              {formData.initials && <p className="mt-2 text-sm text-green-600 font-medium">Initials captured</p>}
            </div>

            {/* Signature */}
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 border border-gray-200">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-9 h-9 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">2</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Your Signature</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Your signature confirms acceptance of all terms in this employment offer.
                  </p>
                </div>
              </div>
              <SignaturePad
                label="Your Signature"
                onSave={(signatureData) => setFormData({...formData, signature: signatureData})}
              />
              {formData.signature && <p className="mt-2 text-sm text-green-600 font-medium">Signature captured</p>}
            </div>

            {/* Name & Date */}
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 border border-gray-200">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">3</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Printed Name & Date</h3>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Printed Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.printedName}
                    onChange={(e) => setFormData({...formData, printedName: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                    placeholder="Your full legal name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  />
                </div>
              </div>
            </div>

            {/* Status + Submit */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex gap-4 text-sm">
                  <span className={formData.initials ? 'text-green-600' : 'text-slate-500'}>
                    {formData.initials ? 'Initials provided' : 'Initials needed'}
                  </span>
                  <span className={formData.signature ? 'text-green-600' : 'text-slate-500'}>
                    {formData.signature ? 'Signed' : 'Signature needed'}
                  </span>
                  <span className={formData.printedName ? 'text-green-600' : 'text-slate-500'}>
                    {formData.printedName ? 'Name entered' : 'Name needed'}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setCurrentStep('review')}
                    className="px-5 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  >
                    Back to Review
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !formData.initials || !formData.signature || !formData.printedName}
                    className="px-8 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Accept & Submit
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferLetterSigning;
